import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  isNilOrZero,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import MonthPicker from 'core/components/DateTimePicker/MonthPicker';
import ToolTip from 'core/components/Tooltip';
import {
  useMedia,
} from 'core/hooks';
import CCMIcon from 'core/icons/CCMIcon';
import {
  PatientDTO,
} from 'dtos';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import dayjs from 'utils/dayjs';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import PatientHeader from 'pages/Dashboard/components/PatientHeader';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import usePanelState from 'pages/Dashboard/hooks/usePanelState';
import {
  useGetPCCActivationSettings,
  useGetPCCPatientDetails,
  useGetPCCStatusByPatientId,
} from 'views/EMR/hooks/pcc';
import {
  useGetPatientInfo,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import PatientActivityContent from 'pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/PatientActivityContent';
import PatientCcmContent from 'pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/PatientCcmContent';
import PatientProgramContent from 'pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/PatientProgramContent';
import {
  PCCPatientActionContext,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';
import PatientTrendsContent from 'pages/Dashboard/pages/Dashboard/RTM/Trends/PatientTrendsContent';
import TempoPatientDetailContent from 'pages/Dashboard/pages/Dashboard/Tempo/Details/TempoPatientDetailContent';
import {
  getEligiblePCCPrograms,
  getPCCPrograms,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/utils';
import {
  parsePCCMonth,
  parsePCCTab,
  PCC_ACTIVITY_TAB,
  PCC_MONTH_FORMAT,
  PCCPanelInfo,
  PCCPanelTab,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/pccPanelUrl';

const ACTIVITY_TAB_KEY = PCC_ACTIVITY_TAB;

type PanelTabKey = PCCPanelTab;

function getPanelTabs(
  isCcmApplicable: boolean,
): Array<{ key: PanelTabKey; label: string }> {
  const ccmTab = isCcmApplicable
    ? [{
      Icon: CCMIcon,
      key: PCCProgram.CCM,
      label: 'CCM',
    }]
    : [];

  return [{
    key: ACTIVITY_TAB_KEY,
    label: 'Activity',
  },
  {
    key: PCCProgram.Tempo,
    label: 'Tempo',
  },
  {
    key: PCCProgram.RTM,
    label: 'RTM',
  },
  ...ccmTab];
}

function isMonthPickerSupported(tabKey: PanelTabKey): boolean {
  return (
    tabKey === ACTIVITY_TAB_KEY
    || tabKey === PCCProgram.Tempo
    || tabKey === PCCProgram.CCM
  );
}

export default function PCCSidePanel({
  entity,
  onClose,
  slots,
}: SidePanelComponentProps<PCCPanelInfo>) {
  const { patientId } = entity;
  const { data: activationSettings } = useGetPCCActivationSettings();
  const isCcmApplicable = activationSettings?.isCcmApplicable !== false;
  const tabs = useMemo(
    () => getPanelTabs(isCcmApplicable),
    [isCcmApplicable],
  );
  const [tab, setTab] = usePanelState<PanelTabKey>({
    param: 'tab',
    parse: parsePCCTab,
    serialize: String,
    defaultValue: ACTIVITY_TAB_KEY,
  });
  const [month, setMonth] = usePanelState({
    param: 'month',
    parse: parsePCCMonth,
    serialize: String,
    defaultValue: entity.month,
    // Keep the month in a link even when it's the current one
    removeDefault: false,
  });
  // CCM may be in a link while it isn't applicable to this org
  const activeTabKey = tab === PCCProgram.CCM && !isCcmApplicable ? ACTIVITY_TAB_KEY : tab;
  const activeTab = Math.max(tabs.findIndex(({ key }) => key === activeTabKey), 0);
  const dateRange = useMemo(() => {
    const date = dayjs(month, PCC_MONTH_FORMAT);

    return {
      min: date.startOf('month').toDate(),
      max: date.endOf('month').toDate(),
    };
  }, [month]);
  const { data: patient, isFetching: isLoading } = useGetPatientInfo(patientId);
  const patientSummary = (patient ?? {}) as Partial<PatientDTO>;
  const { data: patientStatus } = useGetPCCStatusByPatientId(patientId);
  const enrollablePrograms = useMemo(
    () => getEligiblePCCPrograms(getPCCPrograms(patientStatus, isCcmApplicable))
      .map((program) => program.type),
    [patientStatus, isCcmApplicable],
  );
  const patientActionContext: PCCPatientActionContext = {
    patientId,
    patientEmail: patientSummary.email,
    patientPhone: patientSummary.phoneNumber?.phoneNumberWithAreaCode,
    programs: enrollablePrograms,
  };
  const {
    data: patientDetails,
    isFetching: isPatientDetailsFetching,
    isError: isPatientDetailsError,
    refetch: refetchPatientDetails,
  } = useGetPCCPatientDetails(patientId, dateRange);
  const { requestMedia } = useMedia();
  const activeProgramDetail = patientDetails?.programs?.find(
    (programDetail) => programDetail.program === activeTabKey,
  );

  useEffect(() => {
    if (!isNilOrZero(patientId)) {
      requestMedia([patientId]);
    }
  }, [patientId, requestMedia]);

  const isDateRangeSupported = useMemo(() =>
    isMonthPickerSupported(activeTabKey)
  , [activeTabKey]);

  return (
    <div className="flex flex-col justify-between bg-white rounded-xl overflow-hidden h-full w-full">
      <div className="flex flex-col flex-1 h-full w-[40rem]">
        <div className="w-full border-b border-gray-200">
          <div className="flex items-start justify-between w-full">
            <PatientHeader
              isLoading={isLoading}
              patient={patientSummary}
              onClose={onClose}
              actions={slots?.actions}
            />
          </div>
          <div className="flex w-full items-center gap-2 px-4">
            <NavigationTabs
              tabs={tabs}
              activeIndex={activeTab}
              className="min-w-0 flex-1 py-2 !min-h-[unset]"
              onClick={(index) => { setTab(tabs[index]?.key ?? ACTIVITY_TAB_KEY); }}
            />
            <ToolTip
              title="Date range not applicable"
              hide={isDateRangeSupported}
            >
              <div>
                <MonthPicker
                  disabled={!isDateRangeSupported}
                  value={dayjs(month, PCC_MONTH_FORMAT)}
                  placeholder="MM/YYYY"
                  onChange={(value) => {
                    if (isNil(value)) {
                      return;
                    }
                    setMonth(value.format(PCC_MONTH_FORMAT));
                  }}
                />
              </div>
            </ToolTip>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeTabKey === ACTIVITY_TAB_KEY && (
            <PatientActivityContent
              data={patientDetails}
              dateRange={dateRange}
              isFetching={isPatientDetailsFetching}
              isError={isPatientDetailsError}
              onRefetch={refetchPatientDetails}
              patient={patientActionContext}
            />
          )}
          {activeTabKey === PCCProgram.RTM && (
            <PatientProgramContent
              patient={patientActionContext}
              program={PCCProgram.RTM}
              programDetail={activeProgramDetail}
              isFetching={isPatientDetailsFetching}
              isError={isPatientDetailsError}
              onRetry={refetchPatientDetails}
            >
              <PatientTrendsContent patientId={patientId} />
            </PatientProgramContent>
          )}
          {isCcmApplicable && activeTabKey === PCCProgram.CCM && (
            <PatientProgramContent
              patient={patientActionContext}
              program={PCCProgram.CCM}
              programDetail={activeProgramDetail}
              isFetching={isPatientDetailsFetching}
              isError={isPatientDetailsError}
              onRetry={refetchPatientDetails}
            >
              <PatientCcmContent
                patientId={patientId}
                dateRange={dateRange}
              />
            </PatientProgramContent>
          )}
          {activeTabKey === PCCProgram.Tempo && (
            <PatientProgramContent
              patient={patientActionContext}
              program={PCCProgram.Tempo}
              programDetail={activeProgramDetail}
              isFetching={isPatientDetailsFetching}
              isError={isPatientDetailsError}
              onRetry={refetchPatientDetails}
            >
              <TempoPatientDetailContent
                patientId={patientId}
                dateRange={dateRange}
                showPatientSummary={false}
              />
            </PatientProgramContent>
          )}
        </div>
      </div>
    </div>
  );
}
